import { useCallback } from 'react';

import { useTracker, type TrackingEvent } from '@archipro-website/tracker';

import type { QuestionnaireEvent } from './types';

/**
 * Funnel events for the questionnaire engine, in one place so every flow built
 * on it reports the same shape.
 *
 * The brief had no events of its own at all: the only thing measured was the
 * auth segment inside it, so drop-off between questions was invisible. That
 * matters more than usual here, because Get Matched feeds the enquiry term the
 * business steers on, and the account wall has just moved to the front of the
 * flow — without a per-step funnel there is no way to tell whether that helped.
 *
 * `questionnaireId` identifies the flow one-for-one and travels as `Source`, so
 * it is the series key on every chart. Everything else rides inside `ExtraData`:
 * only those two custom keys survive the warehouse pipeline.
 */
const EVENT_NAME: Record<QuestionnaireEvent['type'], TrackingEvent> = {
    opened: 'QuestionnaireOpened',
    'auth-required': 'QuestionnaireAuthRequired',
    'auth-completed': 'QuestionnaireAuthCompleted',
    'step-viewed': 'QuestionnaireStepViewed',
    'step-answered': 'QuestionnaireStepAnswered',
    'step-back': 'QuestionnaireStepBack',
    submitted: 'QuestionnaireSubmitted',
    'submit-failed': 'QuestionnaireSubmitFailed',
    abandoned: 'QuestionnaireAbandoned',
};

/**
 * Bridges engine events onto the tracker.
 *
 * Pass the result as the engine's `onEvent`. The identity is stable, so it does
 * not re-arm the engine's one-shot reporting on every render.
 */
export const useQuestionnaireTracking = (): ((
    event: QuestionnaireEvent,
) => void) => {
    const tracker = useTracker();

    return useCallback(
        (event: QuestionnaireEvent) => {
            if (typeof window === 'undefined') return;

            const { type, questionnaireId, ...rest } = event;
            const payload = { questionnaireId, ...rest };

            void tracker.log(EVENT_NAME[type], {
                url: new URL(window.location.href),
                data: {
                    ...payload,
                    Source: questionnaireId,
                    ExtraData: JSON.stringify(payload),
                },
            });
        },
        [tracker],
    );
};
